import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Signal, inject, signal } from '@angular/core';
import { environment } from '@core/environments/environment';
import { Pagination, PaginationModel } from '@core/models/pagination.model';
import { Observable, finalize } from 'rxjs';
import { LoadingService } from './loading.service';
import {
  AllBondProductsResponse,
  CommercialPaperProductResponse,
  FixedDepositListResponse,
  FixedDepositSearchResponse,
  GetSingleBondResponse,
  GetSingleCommercialPaperResponse,
  PMSTypesResponse,
  SearchBondProductsResponse,
  SearchCommercialPaperProductResponse,
  SingleFixedDepositProduct,
  TBillsProductResponse,
  TreasuryBillsListResponse,
  TreasuryBillsSearchResponse,
} from '@core/models/admin/products';
import { AuthService } from './auth.service';
import { CSVRecordData } from '@core/models/admin/account';

interface SingleProduct {
  status: string;
  message: string;
  data: SingleProductData;
}

export interface SingleProductData {
  id: string;
  name: string;
  description: string;
  price?: number;
  unit?: number;
  type: string;
  status: string;
  category: string;
  updated_at: string;
  created_at: string;
  product_image: null;
  pms_sub_category?: null | string;
  currency: string;
  settlement_type: null;
  year_count: null;
  maturity_date: null;
  coupon_rate: null;
  last_coupon_date: null;
  buy_rate: number;
  min_bond_amount: null;
}

interface UpdateProduct {
  id: string;
  name?: string;
  description?: string;
  type?: string;
  status?: string;
  category?: string;
  pms_sub_category: string;
}

interface UpdateProductResponse {
  status: string;
  message: string;
  data: UpdateProductResponseData;
}

interface UpdateProductResponseData {
  id: string;
  name: string;
  description: string;
  price: number;
  unit: number;
  rate: number;
  updated_at: string;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  http = inject(HttpClient);
  authService = inject(AuthService)
  private loadingService = inject(LoadingService);

    private selectedProduct = signal<any>(null);
    

  BASE_URL = environment.ADMIN_BASE_URL + '/v1/product';

  constructor() {
   }

  setSelectedProduct(product: SingleProductData) {    
    this.selectedProduct.set(product);    
  }

  getSelectedProduct(): Signal<SingleProductData> {
    return this.selectedProduct;
  }

  createProduct(productInfo: any): Observable<any> {
    return this.http.post(`${this.BASE_URL}/create-products`, productInfo);
  }

  createPmsProduct(payload: {
    name: string;
    description: string;
    pms_sub_category: string;
  }): Observable<unknown> {
    const uniqueStateUrl = 'create-pms-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post(`${this.BASE_URL}/create-pms-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  
  createMutalFundProduct(payload: any): Observable<unknown> {
    const uniqueStateUrl = 'create-mutual-fund';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post(`${this.BASE_URL}/create-mutual-fund`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  updateMutalFundProduct(payload: any): Observable<unknown> {
    const uniqueStateUrl = 'create-mutual-fund';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post(`${this.BASE_URL}/update-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  // FIXME: create a global product status for the project
  updateStatus(data: {
    product_id: string;
    status: 'public' | 'private' | 'archived';
  }): Observable<unknown> {
    const uniqueStateUrl = 'change-product-status';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post(`${this.BASE_URL}/change-product-status`, data)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getPmsProducts(data: PaginationModel): Observable<PmsProductResponse> {
    const uniqueStateUrl = 'get-pms-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
    const paginationInstance = new Pagination(data);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());
    let path = `${this.BASE_URL}/get-pms-products`;

    return this.http
      .get<PmsProductResponse>(path, { params })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
    getAllCSVRecords(data: PaginationModel): Observable<CSVRecordData> {
    const uniqueStateUrl = 'get-all-csv-records';
    this.loadingService.setLoading(uniqueStateUrl, true);
    const paginationInstance = new Pagination(data);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());
    let path = `${this.BASE_URL}/get-all-csv-records`;
    return this.http
      .get<CSVRecordData>(path, { params })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getMFDProducts(data: PaginationModel): Observable<PmsProductResponse> {
    const uniqueStateUrl = 'get-mutual-fund-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
    const paginationInstance = new Pagination(data);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());
    let path = `${this.BASE_URL}/get-mutual-fund-products`;
    return this.http
      .get<PmsProductResponse>(path, { params })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }


  searchTbillProducts(queryParams: string): Observable<TreasuryBillsSearchResponse> {
    const uniqueStateUrl = 'filter-lcm-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
  
    return this.http
      .get<TreasuryBillsSearchResponse>(`${this.BASE_URL}/search-lcm-products?${queryParams}`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getTreasuryBills(
    pagination: PaginationModel
  ): Observable<TreasuryBillsListResponse> {
    const uniqueStateUrl = 'get-tbill-products';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const paginationInstance = new Pagination(pagination);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());

    return this.http
      .get<TreasuryBillsListResponse>(`${this.BASE_URL}/get-tbill-products`, {
        params,
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  viewProductInfo(productId: string) {
    return this.http.get(`${this.BASE_URL}/view-products-info/${productId}`);
  }

  getSingleProduct(productId: string): Observable<SingleProduct> {
    const uniqueStateUrl = 'get-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<SingleProduct>(`${this.BASE_URL}/get-product?id=${productId}`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getSingleTBillsProduct(productId: string): Observable<TBillsProductResponse> {
    const uniqueStateUrl = 'get-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<TBillsProductResponse>(
        `${this.BASE_URL}/get-product?product_id=${productId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getPMSComposition(productId: string): Observable<SingleProduct> {
    return this.http.get<SingleProduct>(
      `${this.BASE_URL}/get-product-pms-sum?product_id=${productId}`
    );
  }

  getPMSCompositionList(productId: string): Observable<any> {
    return this.http.get(
      `${this.BASE_URL}/get-product-pms-composition?product_id=${productId}`
    );
  }
  _getPMSCompositionList(productId: string): Observable<any> {
    return this.http.get(`${this.BASE_URL}/get-all-pms?pms_id=${productId}`);
  }

  addComposition(payload: any): Observable<any> {
    return this.http.post(`${this.BASE_URL}/create-pms`, payload);
  }
  deletePMSCompositon(id: string): Observable<any> {
    return this.http.post(`${this.BASE_URL}/delete-pms`, { id });
  }

  updateSingleProduct(
    productPayload: UpdateProduct
  ): Observable<UpdateProductResponse> {
    const uniqueStateUrl = 'change-product-status';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<UpdateProductResponse>(
        `${this.BASE_URL}/update-product`,
        productPayload
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  // convert the 'buy_rate' by multiplying it by 100
  /* 
  The type of the product. Valid values are "tbill_base" for TBill and "commercial_paper" for commercial paper 
  */
  //  payload: {
  //   name: string;
  //   settlement_type: 0 | 1 | 2;
  //   maturity_date: string;
  //   buy_rate: number;
  //   currency: 'NGN' | 'USD';
  //   type: 'tbill_base' | 'commercial_paper';
  // }

  /**
   * Creates a new T-bill product.
   *
   * @param {Object} formData - The form data containing the details of the T-bill product.
   * @param {string} formData.name - The name of the T-bill product.
   * @param {0|1|2} formData.settlement_type - The type of settlement for the T-bill product.
   * @param {string} formData.maturity_date - The maturity date of the T-bill product.
   * @param {number} formData.buy_rate - The investment rate of the T-bill product.
   * @param {'NGN'|'USD'} formData.currency - The currency of the T-bill product.
   * @param {'tbill_base'|'commercial_paper'} formData.type - The type of the T-bill product.
   * @returns {Observable<unknown>} An Observable that emits the response from the server.
   */
  createTbillProduct(formData: any): Observable<unknown> {
    const uniqueStateUrl = 'create-tbill-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    // convert the 'buy_rate'
    const payload = Object.assign(formData, {
      buy_rate: parseFloat(Number(formData.buy_rate).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/create-tbill-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updateTbillProduct(formData: any): Observable<unknown> {
    const uniqueStateUrl = 'update-tbill-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    // convert the 'buy_rate'
    const payload = Object.assign(formData, {
      buy_rate: parseFloat(Number(formData.buy_rate).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/update-tbill-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  searchFixedDepositProducts(queryParams: string): Observable<FixedDepositSearchResponse> {
    const uniqueStateUrl = 'filter-lcm-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
  
    return this.http
      .get<FixedDepositSearchResponse>(`${this.BASE_URL}/search-lcm-products?${queryParams}`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getAllFixedDepositProducts(
    pagination: PaginationModel
  ): Observable<FixedDepositListResponse> {
    const uniqueStateUrl = 'get-all-fixed-deposit-products';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const paginationInstance = new Pagination(pagination);
    const params = new HttpParams()
      .set('category', 'LCM')
      .set('type', 'rate_base')
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());

    return this.http
      .get<FixedDepositListResponse>(`${this.BASE_URL}/get-fixed-deposit-product`, {
        params,
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  createFixedDepositProduct(payload: {
    name: string;
    description: string;
    currency: string;
    type: 'rate_base' | 'high_yield' | string;
  }): Observable<unknown> {
    const uniqueStateUrl = 'create-fixed-deposit-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post(`${this.BASE_URL}/create-products`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updateFixedDepositProduct(payload: {
    product_id: string;
    name: string;
    description: string;
    type: 'unit_base' | 'high_yield';
    currency: string;
  }): Observable<unknown> {
    const uniqueStateUrl = 'update-fixed-deposit-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post(`${this.BASE_URL}/update-fixed-deposit-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getFixedDepositProduct(
    productId: string
  ): Observable<SingleFixedDepositProduct> {
    const uniqueStateUrl = 'get-fixed-deposit-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<SingleFixedDepositProduct>(
        `${this.BASE_URL}/get-product?product_id=${productId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

 searchBondProducts(queryParams: string): Observable<SearchBondProductsResponse> {
    const uniqueStateUrl = 'filter-lcm-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
  
    return this.http
      .get<SearchBondProductsResponse>(`${this.BASE_URL}/search-lcm-products?${queryParams}`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getAllBondsProducts(
    pagination: PaginationModel
  ): Observable<AllBondProductsResponse> {
    const uniqueStateUrl = 'get-all-bond-products';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const paginationInstance = new Pagination(pagination);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());

    return this.http
      .get<AllBondProductsResponse>(`${this.BASE_URL}/get-bond-products`, {
        params,
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  createBondProduct(formData: {
    name: string;
    settlement_type: number;
    year_count: string;
    last_coupon_date: string;
    buy_price: string;
    currency: string;
    coupon_rate: number;
    min_bond_amount: number;
  }): Observable<unknown> {
    const uniqueStateUrl = 'create-bond-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const payload = Object.assign(formData, {
      buy_price: parseFloat(Number(formData.buy_price).toFixed(2)) * 100,
      coupon_rate: parseFloat(Number(formData.coupon_rate).toFixed(2)) * 100,
      min_bond_amount:
        parseFloat(Number(formData.min_bond_amount).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/create-bond-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updateBondProduct(formData: {
    settlement_type: number;
    year_count: number;
    name: string;
    last_coupon_date: string;
    status: string;
    coupon_rate: string;
    currency: string;
    buy_price: string;
    product_id: string;
    min_bond_amount: number;
  }): Observable<unknown> {
    const uniqueStateUrl = 'update-bond-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const payload = Object.assign(formData, {
      buy_price: parseFloat(Number(formData.buy_price).toFixed(2)) * 100,
      coupon_rate: parseFloat(Number(formData.coupon_rate).toFixed(2)) * 100,
      min_bond_amount:
        parseFloat(Number(formData.min_bond_amount).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/update-bond-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getSingleBondProduct(productId: string): Observable<GetSingleBondResponse> {
    const uniqueStateUrl = 'get-single-bond-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<GetSingleBondResponse>(
        `${this.BASE_URL}/get-product?product_id=${productId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }


  searchCommercialPaperProducts(queryParams: string): Observable<SearchCommercialPaperProductResponse> {
    const uniqueStateUrl = 'filter-lcm-products';
    this.loadingService.setLoading(uniqueStateUrl, true);
  
    return this.http
      .get<SearchCommercialPaperProductResponse>(`${this.BASE_URL}/search-lcm-products?${queryParams}`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getAllCommercialPaperProducts(
    pagination: PaginationModel
  ): Observable<CommercialPaperProductResponse> {
    const uniqueStateUrl = 'get-commercial-paper';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const paginationInstance = new Pagination(pagination);
    const params = new HttpParams()
      .set('take', paginationInstance.size.toString())
      .set('skip', paginationInstance.skip.toString());

    return this.http
      .get<CommercialPaperProductResponse>(
        `${this.BASE_URL}/get-commercial-paper`,
        { params }
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  /**
   * Creates a new Commercial Paper product.
   *
   * @param {Object} formData - The form data containing the details of the Commercial Paper product.
   * @param {string} formData.name - The name of the Commercial Paper product.
   * @param {0|1|2} formData.settlement_type - The type of settlement for the Commercial Paper product.
   * @param {string} formData.maturity_date - The maturity date of the Commercial Paper product.
   * @param {number} formData.buy_rate - The investment rate of the Commercial Paper product.
   * @param {'NGN'|'USD'} formData.currency - The currency of the Commercial Paper product.
   * @param {'tbill_base'|'commercial_paper'} formData.type - The type of the Commercial Paper product.
   * @returns {Observable<unknown>} An Observable that emits the response from the server.
   */
  createCommercialPaperProduct(formData: any): Observable<unknown> {
    const uniqueStateUrl = 'create-commercial-paper-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const payload = Object.assign(formData, {
      buy_rate: parseFloat(Number(formData.buy_rate).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/create-tbill-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  /**
   * Updates the Commercial Paper product.
   *
   * @param {Object} formData - The options to update the product.
   * @param {Number} [formData.settlement_type] - The type of settlement for the product.
   * @param {String} [formData.name] - The name of the product.
   * @param {String} [formData.maturity_date] - The maturity date of the product.
   * @param {String} [formData.status] - The status of the product.
   * @param {String} [formData.currency] - The currency of the product.
   * @param {Number} [formData.buy_rate] - The investment rate of the product.
   * @param {'tbill_base'|'commercial_paper'} [formData.type] - The type of the product.
   * @param {String} formData.product_id - The ID of the product to be updated. Required.
   * @returns {Observable<unknown>} An Observable that emits the response from the server.
   */
  updateCommercialPaperProduct(formData: any): Observable<unknown> {
    const uniqueStateUrl = 'update-commercial-paper-product';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const payload = Object.assign(formData, {
      buy_rate: parseFloat(Number(formData.buy_rate).toFixed(2)) * 100,
    });

    return this.http
      .post(`${this.BASE_URL}/update-tbill-product`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getCommercialPaperProduct(
    productId: string
  ): Observable<GetSingleCommercialPaperResponse> {
    const uniqueStateUrl = 'get-commercial-paper-product';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<GetSingleCommercialPaperResponse>(
        `${this.BASE_URL}/get-product?product_id=${productId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getPmsTypes(): Observable<PMSTypesResponse> {
    const uniqueStateUrl = 'get-pms-types';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .get<PMSTypesResponse>(`${this.BASE_URL}/get-pms-types`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getGlobalProducts(): Observable<GlobalProductsResponse> {
    const uniqueStateUrl = 'get-global-products';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .get<GlobalProductsResponse>(`${this.BASE_URL}/get-global-products`)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
}

export interface PmsProductResponse {
  status: string;
  message: string;
  data: PmsProductData;
}

export interface PmsProductData {
  total_count: number;
  data: PmsProductDatum[];
}

export interface PmsProductDatum {
  name: string;
  description: string;
  id: string;
  type: string;
  status: string;
  product_image: null;
  pms_sub_category: null | string;
  category: string;
  currency: null;
  created_at: string;
  updated_at: string;
}

export interface GlobalProductsResponse {
  status: string;
  message: string;
  data: GlobalProductDatum[];
}

export interface GlobalProductDatum {
  id: string;
  name: string;
  description: string;
  category: string;
  currency: string | null;
}
